import { moneyWithCommas } from "../helpers/RegexHelper";
import TournamentReportingOrderItem from "../components/tournament/tournament-reporting/tournament-reporting-order-item";
import TournamentOrderFormResponsesCard from "../components/tournament/tournament-reporting/tournament-order-form-responses-card";
import { faArrowRight, faDollarSign } from "@fortawesome/free-solid-svg-icons";
import { getShortMonthDate } from "../helpers/Dates";
import {
  defaultGapping,
  defaultLayouts,
  defaultTypography,
  validations,
} from "golfstatus_react_components";

const { validEmail, validPhone } = validations;

export const rowValue = (value, description) => {
  return (
    <div
      style={{
        ...defaultLayouts.vStack.align("flex-end", "center"),
        ...defaultGapping.mediumGap,
      }}
    >
      <div>{value}</div>
      {description ? (
        <div style={{ ...defaultTypography.bodyRegular.withOpacity(0.7) }}>
          {description}
        </div>
      ) : null}
    </div>
  );
};

export const getReportingForm = (context, currentOrder) => {
  if (context && currentOrder) {
    let orderDetailsFields = [
      ...(currentOrder?.registrationOrderItems?.map?.((roi) => ({
        label: `${roi.tournamentPackage.name}`,
        value: roi?.quantity > 1 ? rowValue(
          moneyWithCommas(roi?.totalCost),
          `(${roi?.quantity} x ${moneyWithCommas(roi?.unitCost)})`
        ) : moneyWithCommas(roi?.totalCost),
        type: "row",
        cost: roi.tournamentPackage.packageCost,
      })) ?? []),
    ];

    // sort package items from largest cost to smallest
    orderDetailsFields.sort((a, b) => {
      return b.cost - a.cost;
    });

    if (currentOrder?.discountCodes?.length > 0) {
      orderDetailsFields.push(
        {
          label: "Subtotal",
          type: "row",
          value: moneyWithCommas(currentOrder?.totalCostBeforeDiscounts),
        },
        {
          label: `Discount ${
            currentOrder.discountCodes?.length === 0
              ? ""
              : `code ${currentOrder.discountCodes?.[0]?.code}`
          }`,
          type: "row",
          value: `- ${moneyWithCommas(currentOrder?.discountAmount)}`,
        }
      );
    }

    if (currentOrder?.donationOrderItems?.length > 0) {
      let donationFields = currentOrder?.donationOrderItems?.map?.(
        (donation) => ({
          label: donation?.roundUp ? "Round Up" : "donation",
          value: moneyWithCommas(donation.usdTotalCost),
          type: "row",
        })
      );

      orderDetailsFields.push(...donationFields);
    }

    // add fees
    if (currentOrder?.registrantCoversFees  && currentOrder.nonRefundableFees > 0 && currentOrder.feesPaidByRegistrant !== "0.0") {

      orderDetailsFields.push({
        label: "Fee",
        type: "row",
        value: rowValue(
          moneyWithCommas(currentOrder?.totalPlatformFee),
          !currentOrder.voidable
            ? `Paid by ${
                currentOrder?.registrantCoversFees && currentOrder.feesPaidByRegistrant !== "0.0"
                  ? currentOrder?.registrationCustomer?.fullName
                  : context?.tournament?.organization?.name
              }`
            : null
        ),
      });
    }

    if (currentOrder?.internationalCardUsed) {
      orderDetailsFields.push({
        label: "International Card Fee",
        type: "row",
        value: moneyWithCommas(currentOrder?.internationalFee),
      });
    }

    orderDetailsFields.push({
      label: "Total",
      type: "row",
      value: moneyWithCommas(currentOrder?.purchaseCost),
    });

    const totalRefundAmount = currentOrder?.registrationOrderRefunds?.reduce(
      (total, refund) => total + (refund?.amount || 0),
      0
    );

    let refundFields = [];

    if (currentOrder?.registrationOrderRefunds?.length > 0) {
      let refundRows = currentOrder?.registrationOrderRefunds?.map(
        (refund) => ({
          label: `Refund - ${getShortMonthDate(refund.updatedAt)}`,
          type: "row",
          value: `- ${moneyWithCommas(refund?.amount)}`,
        })
      );
      refundFields?.push?.(...refundRows);
    }

  

    if ( currentOrder.nonRefundableFees > 0 && currentOrder.feesPaidByRegistrant === "0.0") {

      refundFields.push({
        label: "Fee",
        type: "row",
        value: rowValue(
          moneyWithCommas(currentOrder?.totalPlatformFee),
          !currentOrder.voidable
            ? `Paid by ${
                currentOrder?.registrantCoversFees && currentOrder.feesPaidByRegistrant !== "0.0"
                  ? currentOrder?.registrationCustomer?.fullName
                  : context?.tournament?.organization?.name
              }`
            : null
        ),
      });
    }
    
    refundFields?.push?.({
      label: "Refundable amount",
      type: "row",
      value: rowValue(
        moneyWithCommas(
          currentOrder?.paidElsewhere
            ? 0
            : currentOrder?.netProfit - totalRefundAmount
        ),
        "The fee is non-refundable"
      ),
    });

    let form = [
      {
        type: "vertical large-gap",
        extras: (
          <TournamentReportingOrderItem
            {...currentOrder}
          ></TournamentReportingOrderItem>
        ),
      },
    ];

    if (context?.orderForms?.length > 0) {
      form.push({
        type: "vertical large-gap",
        title: "Form Responses",
        sectionActions: [
          {
            buttonTitle: "View Responses",
            rightIcon: faArrowRight,
            type: "black no-wrap mobile-icon",
            actionClick: context?.viewResponses,
          },
        ],
        fields: [
          {
            customView: true,
            value: (
              <TournamentOrderFormResponsesCard
                forms={context.orderForms}
                onSelect={context?.viewResponses}
              />
            ),
          },
        ],
      });
    }

    if (currentOrder?.status !== "draft") {
      form.push({
        type: "vertical xx-large-gap row-fields",
        title: "Order Details",
        fields: orderDetailsFields,
      });

      if (
        currentOrder?.paymentType !== "customer_invoice" &&
        !currentOrder?.voidable && currentOrder.nonRefundableFees > 0
      ) {
        form.push({
          type: "vertical xx-large-gap row-fields",
          title: "Refund Details",
          fields: refundFields,
        });
      }
    }

    return form;
  }
};

export const getRegistrationCustomerEditForm = (context, options = {}) => {
  if (!context) return [];
  const { showCompanyName = false } = options;

  const fields = [];

  if (showCompanyName) {
    fields.push({
      label: "Company Name",
      ...context.setBinding("companyName"),
      isEditable: true,
      placeholder: "Company Name (Optional)",
    });
  }

  fields.push(
    {
      label: "First Name",
      ...context.setBinding("firstName"),
      isEditable: true,
      placeholder: "First Name",
      required: true,
    },
    {
      label: "Last Name",
      ...context.setBinding("lastName"),
      isEditable: true,
      placeholder: "Last Name",
      required: true,
    },
    {
      label: "Email Address",
      ...context.setBinding("email"),
      validation: {
        isValid: validEmail,
        invalidLabel: "Not a valid email address",
      },
      isEditable: true,
      placeholder: "Email Address",
      required: true,
    },
    {
      label: "Phone Number",
      ...context.setBinding("phone"),
      validation: {
        isValid: validPhone,
        invalidLabel: "Not a valid phone number",
      },
      isEditable: true,
      placeholder: "Phone Number",
      required: true,
    }
  );

  return [
    {
      type: "vertical xx-large-gap",
      title: "Contact Details",
      fields,
    },
  ];
};

export const getPayoutRequestForm = (context) => {
  if (context) {
    return [
      {
        type: "vertical large-gap",
        fields: [
          //{label: "Paid By",placeholder: "Paid By", value:<GSRadioGroup options={[{label: "Check"},{label: "Write Off"},{label: "Balance Transfer"}, {label: "Golfstatus Fees"}]}/>, customView: true, isEditable: true  },
          {
            label: "Payout Amount",
            placeholder: "0.00",
            leftIcon: faDollarSign,
            value: context?.getData?.()?.amount,
            onChange: (e) => {
              context?.updateData?.(e.target.value, "amount");
            },
            isEditable: true,
            failedValidation: context.validationFailed,
            required: true,
          },
          {
            label: "Note",
            placeholder: "Note",
            value: context?.getData?.()?.memo,
            onChange: (e) => {
              context?.updateData?.(e.target.value, "memo");
            },
            type: "text-area",
            rows: 5,
            isEditable: true,
          },
        ],
      },
    ];
  }
};
